import { createContext, useEffect, useState } from 'react';
import { User } from '../models/user';
import { RegisterFormInputDto } from '../models/dto/registerDto';
import axios from 'axios';
import jwtDecode from 'jwt-decode';

export interface AuthContextData {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => void;
  logout: () => void;
  register: (registerInput: RegisterFormInputDto) => void;
}

export const AuthContext = createContext<AuthContextData>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
  register: () => {},
});

export const AuthProvider = (props: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!user);
  const [isLoading, setIsLoading] = useState(true);

  const register = async (registerInput: RegisterFormInputDto) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASEURL}users/register`,
        registerInput,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw new Error('Register request failed');
    }
  };

  const login = async (enteredEmail: string, enteredPassword: string) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASEURL}users/login`,
        {
          email: enteredEmail,
          password: enteredPassword,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const { firstName, lastName, email, accessToken } =
        response.data?.loginTokenData;

      setUser({
        firstName,
        lastName,
        email,
      } as User);

      setIsAuthenticated(true);
      localStorage.setItem('access_token', accessToken);

      return response.data;
    } catch (error) {
      throw new Error('Invalid Login Credential!');
    }
  };

  const logout = async () => {
    // const refreshToken = localStorage.getItem('refresh_token');
    // if (!refreshToken) return;

    // await axios.post(`${process.env.REACT_APP_API_BASEURL}logout`);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');

    if (token && token.split('.').length === 3) {
      try {
        setIsAuthenticated(true);
        setUser({
          ...jwtDecode(token),
        } as User);
      } catch (error) {
        console.error('Invalid JWT Token', error);
        logout();
      }
    } else {
      logout();
    }
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isLoading, login, logout, register }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
